import {
  ControlMessage,
  getUser,
  initialize,
  openSession,
  showInAppMessage,
  subscribeToInAppMessage,
} from '@braze/web-sdk'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { captureUtms, getStoredUtms } from '@/lib/attribution/landingUtms'
import { hasMarketingConsent } from '@/lib/consent'

type BrazeContextType = {
  initialized: boolean
  suppressInAppMessages: () => void
  resumeInAppMessages: () => void
  setUserAttribute: (key: string, value: string | boolean | number | object) => void
}

const BrazeContext = createContext<BrazeContextType | undefined>(undefined)

// e2e opt-out: a dedicated cookie lets automated test runs disable ALL Braze
// in-app pop-ups so live campaign content never fires mid-test. Gated so it can
// NEVER take effect on the production site (zoe.com) — only stage.zoe.com / local
// dev — mirroring the optimizelyOptOut opt-out cookie.
const isBrazeE2eOptOut = () => {
  if (process.env.NEXT_PUBLIC_ZOE_ENV === 'PROD') return false
  if (typeof window === 'undefined') return false
  const { hostname } = window.location
  if (hostname === 'zoe.com' || hostname === 'www.zoe.com') return false
  return /(?:^|; )x-zoe-braze-opt-out=true(?:;|$)/.test(document.cookie)
}

export const BrazeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [initialized, setInitialized] = useState(false)

  const suppressRef = useRef(false)
  const initializedRef = useRef(false)

  const suppressInAppMessages = useCallback(() => {
    suppressRef.current = true
  }, [])

  const resumeInAppMessages = useCallback(() => {
    suppressRef.current = false
  }, [])

  const setUserAttribute = useCallback((key: string, value: string | boolean | number | object) => {
    const user = getUser()
    user?.setCustomUserAttribute(key, value)
  }, [])

  const initBraze = useCallback(() => {
    if (initializedRef.current) return
    if (process.env.ENABLE_BRAZE !== 'true') return

    const brazeApiKey = process.env.NEXT_PUBLIC_BRAZE_API_KEY
    const brazeSdkEndpoint = process.env.BRAZE_SDK_ENDPOINT

    if (!brazeApiKey || !brazeSdkEndpoint) return
    // Braze is a marketing/personalisation platform and must only initialise once the user
    // has granted marketing consent under PECR.
    if (!hasMarketingConsent()) return

    initializedRef.current = true

    initialize(brazeApiKey, {
      baseUrl: brazeSdkEndpoint,
      enableLogging: process.env.NEXT_PUBLIC_ZOE_ENV !== 'PROD' && process.env.NEXT_ENABLE_BRAZE_LOGGING === 'true',
      allowUserSuppliedJavascript: true,
    })

    subscribeToInAppMessage((message) => {
      // e2e kill-switch: suppress every in-app pop-up when the opt-out cookie is set.
      if (isBrazeE2eOptOut()) return

      if (message instanceof ControlMessage) {
        showInAppMessage(message)
        return
      }

      if (suppressRef.current) return

      showInAppMessage(message)
    })

    openSession()

    // Set the captured landing UTMs as custom attributes on the anonymous
    // profile (consent-gated via initBraze; whitelist-filtered, never lead_id).
    captureUtms()
    const utms = getStoredUtms()
    if (utms) {
      for (const [key, value] of Object.entries(utms)) {
        if (value) setUserAttribute(key, value)
      }
    }

    setInitialized(true)
  }, [setUserAttribute])

  // Initialise on mount if marketing consent is already granted.
  useEffect(() => {
    if (process.env.ENABLE_BRAZE !== 'true' || initializedRef.current) return

    initBraze()
  }, [initBraze])

  // Initialise once CookieHub resolves consent, and again if the user grants marketing consent later.
  useEffect(() => {
    if (process.env.ENABLE_BRAZE !== 'true') return

    const handleConsentChange = () => {
      initBraze()
    }

    document.addEventListener('cookiehub_onInitialise', handleConsentChange)
    document.addEventListener('cookiehub_onStatusChange', handleConsentChange)
    return () => {
      document.removeEventListener('cookiehub_onInitialise', handleConsentChange)
      document.removeEventListener('cookiehub_onStatusChange', handleConsentChange)
    }
  }, [initBraze])

  const value = useMemo(
    () => ({
      initialized,
      suppressInAppMessages,
      resumeInAppMessages,
      setUserAttribute,
    }),
    [initialized, suppressInAppMessages, resumeInAppMessages, setUserAttribute],
  )

  return <BrazeContext.Provider value={value}>{children}</BrazeContext.Provider>
}

export const useBraze = () => {
  const context = useContext(BrazeContext)
  if (context === undefined) {
    throw new Error('useBraze must be used within a BrazeProvider')
  }
  return context
}

export const useSuppressBrazeInAppMessages = (enabled = true) => {
  const { suppressInAppMessages, resumeInAppMessages } = useBraze()

  useEffect(() => {
    // Suppression of in-app messages is intentionally PROD-only so that staging/preview
    // environments can exercise messages on every page during QA.
    if (process.env.NEXT_PUBLIC_ZOE_ENV !== 'PROD') return
    if (!enabled) return
    suppressInAppMessages()
    return () => resumeInAppMessages()
  }, [enabled, suppressInAppMessages, resumeInAppMessages])
}
